import React, {useMemo, useState} from "react";
import {useQuery} from "@tanstack/react-query";
import {Alert, Box, Breadcrumbs, Link, Stack, Typography} from "@mui/material";
import {useSearchParams} from "react-router-dom";
import {loadProposals, ProposalFilter} from "../service/proposal.service";
import {Proposal} from "../types/Proposal";
import {loadGeoCity, loadGeoCountries} from "../service/geo.service";
import {GeoCity, GeoCountry} from "../types/Geo";
import {
    ANALITICS_PROPOSAL_STATUSES,
    AssetKindCount,
    buildInvestorAnalitics,
    CityCapitalAnalitics,
    DistrictAnalitics,
    formatUsdFromCents,
    PlaceCatalog,
} from "../utils/investorAnalitics";
import {CountryCitiesMap, WorldOperatingMap} from "../components/map/OperatingMaps";
import {Table} from "../components/table/Table";
import {TableHeaderProps} from "../components/table/TableTypes";

// If this page draws a data table, use components/table/Table (same component as Listings).

const ANALITICS_FILTER: ProposalFilter = {
    page: 0,
    size: 200,
    sortKey: "createdAt",
    sortOrder: "desc",
    proposalStatuses: ANALITICS_PROPOSAL_STATUSES,
    light: true,
};

export function CityAnaliticsPage() {
    const [params, setParams] = useSearchParams();
    const countryId = params.get("country") || "";
    const cityId = params.get("city") || "";

    const countriesQuery = useQuery({
        queryKey: ["geo-countries"],
        refetchOnWindowFocus: false,
        queryFn: () => loadGeoCountries().then((r) => r.data),
    });

    const country = useMemo(
        () => (countriesQuery.data || []).find((row) => row.id === countryId),
        [countriesQuery.data, countryId],
    );

    const cityQuery = useQuery({
        queryKey: ["geo-city", cityId],
        enabled: !!cityId,
        refetchOnWindowFocus: false,
        queryFn: () => loadGeoCity(cityId).then((r) => r.data),
    });

    const listingsQuery = useQuery({
        queryKey: ["city-analitics-listings", cityId, cityQuery.data?.lat, cityQuery.data?.lng, cityQuery.data?.radiusKm],
        enabled: !!cityQuery.data,
        refetchOnWindowFocus: false,
        queryFn: () => loadCityListings(cityQuery.data as GeoCity),
    });

    const catalog: PlaceCatalog | undefined = cityQuery.data ? {
        cityName: cityQuery.data.countryName ? cityQuery.data.name + ", " + cityQuery.data.countryName : cityQuery.data.name,
        districts: (cityQuery.data.districts || []).map((d) => ({
            name: d.name,
            aliases: d.aliases,
            lat: d.lat,
            lng: d.lng,
            radiusKm: d.radiusKm,
        })),
    } : undefined;

    const analitics = useMemo(
        () => buildInvestorAnalitics(listingsQuery.data || [], catalog),
        [listingsQuery.data, catalog],
    );

    function selectCountry(next: GeoCountry) {
        setParams({country: next.id});
    }
    function selectCity(next: GeoCity) {
        setParams({country: next.countryId || countryId, city: next.id});
    }
    function backToWorld() {
        setParams({});
    }
    function backToCountry() {
        if (countryId) {
            setParams({country: countryId});
        } else {
            setParams({});
        }
    }

    if (!countryId) {
        return (
            <Box data-testid="city-analitics-page">
                <Typography variant="h5" component="h1" gutterBottom>Dashboard</Typography>
                <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                    Start with the map of countries Relapka covers.
                </Typography>
                {countriesQuery.error ? <Alert severity="error">Failed to load operating countries.</Alert> : null}
                {countriesQuery.isLoading ? <Typography>Loading map…</Typography> : (
                    <WorldOperatingMap
                        countries={Array.isArray(countriesQuery.data) ? countriesQuery.data : []}
                        onSelectCountry={selectCountry}
                    />
                )}
            </Box>
        );
    }

    if (!cityId) {
        return (
            <Box data-testid="city-analitics-page">
                <Typography variant="h5" component="h1" gutterBottom>Dashboard</Typography>
                <Breadcrumbs sx={{mb: 2}}>
                    <Link component="button" underline="hover" onClick={backToWorld}>World</Link>
                    <Typography color="text.primary">{country?.name || "Country"}</Typography>
                </Breadcrumbs>
                {country ? (
                    <CountryCitiesMap country={country} onSelectCity={selectCity}/>
                ) : (
                    <Typography>Loading country…</Typography>
                )}
            </Box>
        );
    }

    const city = cityQuery.data;
    const currentCapital = analitics.capitalByCity[0];

    return (
        <Box data-testid="city-analitics-page">
            <Typography variant="h5" component="h1" gutterBottom>Dashboard</Typography>
            <Breadcrumbs sx={{mb: 2}}>
                <Link component="button" underline="hover" onClick={backToWorld}>World</Link>
                <Link component="button" underline="hover" onClick={backToCountry}>{city?.countryName || country?.name || "Country"}</Link>
                <Typography color="text.primary">{city?.name || "City"}</Typography>
            </Breadcrumbs>
            <Typography variant="body2" color="text.secondary" sx={{mb: 3}}>
                Demand, liquidity, buyable asset types, and purchase capital for listings in this city.
            </Typography>

            {listingsQuery.isLoading && <Typography>Loading listings…</Typography>}
            {listingsQuery.error ? <Alert severity="error">Failed to load listings for analitics.</Alert> : null}

            <Stack spacing={3}>
                <DistrictDemandPanel rows={analitics.districts} loading={listingsQuery.isFetching}/>
                <BuyableAssetsPanel rows={analitics.assetsByCity[0]?.kinds || []} loading={listingsQuery.isFetching}/>
                <CapitalNeededPanel
                    current={currentCapital}
                    rows={analitics.capitalByCity}
                    loading={listingsQuery.isFetching}
                />
            </Stack>
        </Box>
    );
}

function loadCityListings(city: GeoCity): Promise<Proposal[]> {
    const filter: ProposalFilter = {
        ...ANALITICS_FILTER,
        cityId: city.id,
        lat: city.lat,
        lng: city.lng,
        rangeKm: city.radiusKm || 25,
    };
    return loadProposals(filter).then((response) => response.data?.content ?? []);
}

function DistrictDemandPanel({rows, loading}: {rows: DistrictAnalitics[]; loading?: boolean}) {
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(25);
    const headers: TableHeaderProps<DistrictAnalitics>[] = [
        {key: "district", title: "District", render: (row) => row.district},
        {key: "listingCount", title: "Listings", alignment: "right", render: (row) => row.listingCount},
        {key: "soldCount", title: "Sold", alignment: "right", render: (row) => row.soldCount},
        {key: "demandRank", title: "Demand rank", alignment: "right", render: (row) => row.demandRank},
        {key: "liquidityRank", title: "Liquidity rank", alignment: "right", render: (row) => row.liquidityRank},
    ];
    return (
        <Box data-testid="district-demand-liquidity">
            <Typography variant="h6" component="h2" gutterBottom>District demand and liquidity</Typography>
            {rows.length === 0 && !loading ? (
                <Typography color="text.secondary">No listings with a usable district.</Typography>
            ) : (
                <Table
                    data={rows.slice(page * size, page * size + size)}
                    headers={headers}
                    total={rows.length}
                    page={page}
                    size={size}
                    isLoading={loading}
                    pageChange={(nextPage, nextSize) => {
                        setPage(nextPage);
                        setSize(nextSize);
                    }}
                />
            )}
        </Box>
    );
}

function BuyableAssetsPanel({rows, loading}: {rows: AssetKindCount[]; loading?: boolean}) {
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(25);
    const headers: TableHeaderProps<AssetKindCount>[] = [
        {key: "kind", title: "Asset type", render: (row) => row.kind},
        {key: "listingCount", title: "Listings of this type", alignment: "right", render: (row) => row.listingCount},
    ];
    return (
        <Box data-testid="city-asset-kinds">
            <Typography variant="h6" component="h2" gutterBottom>Buyable assets in this city</Typography>
            <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                Apartment types that appear in current listings, and how many listings have that type.
            </Typography>
            {rows.length === 0 && !loading ? (
                <Typography color="text.secondary">No asset types on current listings.</Typography>
            ) : (
                <Table
                    data={rows.slice(page * size, page * size + size)}
                    headers={headers}
                    total={rows.length}
                    page={page}
                    size={size}
                    isLoading={loading}
                    pageChange={(nextPage, nextSize) => {
                        setPage(nextPage);
                        setSize(nextSize);
                    }}
                />
            )}
        </Box>
    );
}

function CapitalNeededPanel({current, rows, loading}: {
    current?: CityCapitalAnalitics;
    rows: CityCapitalAnalitics[];
    loading?: boolean;
}) {
    const headers: TableHeaderProps<CityCapitalAnalitics>[] = [
        {key: "city", title: "City", render: (row) => row.city},
        {key: "listingCount", title: "Listings", alignment: "right", render: (row) => row.listingCount},
        {key: "entryPrice", title: "Entry price", alignment: "right", render: (row) => formatUsdFromCents(row.entryPrice)},
        {key: "typicalPrice", title: "Typical price", alignment: "right", render: (row) => formatUsdFromCents(row.typicalPrice)},
    ];
    return (
        <Box data-testid="capital-needed">
            <Typography variant="h6" component="h2" gutterBottom>Capital needed</Typography>
            {current ? (
                <Typography sx={{mb: 2}}>
                    Entry {formatUsdFromCents(current.entryPrice)}, typical {formatUsdFromCents(current.typicalPrice)}.
                </Typography>
            ) : (
                <Typography color="text.secondary" sx={{mb: 2}}>No priced listings to compute a purchase ticket.</Typography>
            )}
            {rows.length > 0 && (
                <Table
                    data={rows}
                    headers={headers}
                    total={rows.length}
                    page={0}
                    size={Math.max(rows.length, 5)}
                    isLoading={loading}
                    pageChange={() => undefined}
                />
            )}
        </Box>
    );
}
